import React, { useCallback } from "react";
import { RadioCardField } from "@/components/forms/fields";

import { IoCoin } from "@/components/Common/IoCoin/IoCoin";
import { AmericanExpressIcon } from "@/components/Icons/AmericanExpressIcon";
import { CreditCardIcon } from "@/components/Icons/CreditCardIcon";
import { CreditsIcon } from "@/components/Icons/CreditsIcon";
import { MasterCardIcon } from "@/components/Icons/MasterCardIcon";
import { VisaIcon } from "@/components/Icons/VisaIcon";
import ENV_VARIABLES from "@/env";
import { useUserStore } from "@/store/user";
import { getIsCreditsEnabled } from "@/utils/credits";
import { useEffect, useMemo, useRef } from "react";
import { useField } from "formik";

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "@/components/ui/accordion";
import { AnalyticsEventType } from "@/constants/analytics";
import { cn } from "@/lib/utils";
import { useMediaQueryMatch } from "@/hooks/useMediaQueryMatch";
import { AuthMachineContext } from "@/store/auth";
import { trackEvent } from "@/utils/analytics";
import { SimpleTooltip } from "@/components/Common/Tooltip/SimpleTooltip";
import {
  creditsBalancesSatisfyPayment,
  readCreditsBalances
} from "@/components/ClustersPage/VerticalFlowComponents/creditsPaymentAmount";

const USDC_INFO_URL = "https://www.usdc.com/";
const PAYMENT_PROVIDER_FEE_LABEL = "5% Fee";
const ZERO_FEE_LABEL = "0% Fee";

function PaymentOptionFeeBadge({
  label = PAYMENT_PROVIDER_FEE_LABEL,
  className
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "label-x-small w-fit rounded-[4px] border border-gray-dark-200 bg-gray-dark-600 px-2 py-1 text-gray-dark-2200",
        className
      )}
    >
      {label}
    </div>
  );
}

function UsdcPaymentLabel({ className }: { className?: string }) {
  return (
    <a
      href={USDC_INFO_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={cn(
        "min-w-0 text-[16px] font-medium leading-[24px] text-foreground hover:underline",
        className
      )}
      onClick={(e) => e.stopPropagation()}
    >
      USD backed stablecoin
    </a>
  );
}

function CreditCardBrandBox({
  variant,
  className,
  children
}: {
  variant: "light" | "dark";
  className?: string;
  children: React.ReactNode;
}) {
  const borderClass = variant === "dark" ? "border-gray-dark-200" : "border-border";

  return (
    <div
      className={cn(
        "flex h-[22px] w-[32px] shrink-0 items-center justify-center rounded-[4px] border",
        borderClass,
        className
      )}
    >
      {children}
    </div>
  );
}

function CreditCardBrands({
  variant = "light",
  className
}: {
  variant?: "light" | "dark";
  className?: string;
}) {
  const slashClass = variant === "dark" ? "text-gray-dark-2200" : "text-muted-foreground";

  return (
    <div className={cn("flex flex-row items-center justify-center gap-2", className)}>
      <CreditCardBrandBox variant={variant} className="bg-black p-1">
        <VisaIcon className="h-[10px] w-auto max-w-full" />
      </CreditCardBrandBox>
      <span className={cn("subtext-small", slashClass)}>/</span>
      <CreditCardBrandBox variant={variant} className="bg-black p-1">
        <MasterCardIcon className="h-[12px] w-auto max-w-full" />
      </CreditCardBrandBox>
      <span className={cn("subtext-small", slashClass)}>/</span>
      <CreditCardBrandBox variant={variant} className="overflow-hidden p-0">
        <AmericanExpressIcon className="h-full w-full" />
      </CreditCardBrandBox>
    </div>
  );
}

type ClusterPaymentOptionsProps = {
  currency: "iocoin" | "usdc" | "credits" | "stripe";
  totalCost?: number;
  conversionRate?: number;
  allowCryptoPayment?: boolean;
  order?: Array<"credits" | "stripe" | "usdc" | "iocoin">;
  className?: string;
  hideCryptoOptions?: boolean;
  /** When false, credit card stays available even if credits balance covers totalCost. */
  disableCreditCardWhenSufficientCredits?: boolean;
  /**
   * Paid (non-promotional) floor from pricing.
   * When set, Stripe/USDC stay enabled unless total + paid floors are both met.
   */
  minNonPromotionalCreditsRequired?: number;
  /** Full-width single row with an outer border (e.g. modals). */
  bordered?: boolean;
  /** When true, shows fee labels on applicable payment options (e.g. credit card). */
  showPaymentFees?: boolean;
  /** @deprecated Use `bordered` instead. */
  stacked?: boolean;
};

export const ClusterPaymentOptions: React.FC<ClusterPaymentOptionsProps> = ({
  currency,
  totalCost,
  conversionRate,
  allowCryptoPayment = true,
  order = ["stripe", "credits", "usdc", "iocoin"],
  className,
  hideCryptoOptions = false,
  disableCreditCardWhenSufficientCredits = true,
  minNonPromotionalCreditsRequired = 0,
  bordered: borderedProp = false,
  showPaymentFees = false,
  stacked
}) => {
  const bordered = borderedProp || stacked || false;
  const balances = useUserStore((store) => store.balances);
  const fetchBalances = useUserStore((store) => store.fetchBalances);
  const pendingOptionRef = useRef<string | null>(null);
  const lastTrackedKeyRef = useRef<string | null>(null);
  const lastSelectedOptionRef = useRef<string | null>(null);
  const isLoggedIn = AuthMachineContext.useSelector((state) => state.context.isLoggedIn);
  const theme = useUserStore((state) => state.theme);

  useEffect(() => {
    if (isLoggedIn) {
      void fetchBalances(true);
    }
  }, [isLoggedIn, fetchBalances]);

  const isSufficientCredits = useCallback(() => {
    const { creditsBalance, purchasedCreditsBalance, courtesyCreditsBalance } =
      readCreditsBalances(balances);

    const costInUSD =
      currency === "iocoin" && conversionRate
        ? totalCost! / parseFloat(conversionRate.toFixed(2))
        : totalCost;

    if (!costInUSD || costInUSD <= 0) {
      return false;
    }

    return creditsBalancesSatisfyPayment({
      totalCost: costInUSD,
      creditsBalance,
      purchasedCreditsBalance,
      minNonPromotionalCreditsRequired,
      courtesyCreditsBalance
    });
  }, [balances, totalCost, currency, conversionRate, minNonPromotionalCreditsRequired]);

  const isUSDCPaymentDisabled = useMemo(() => {
    if (!isLoggedIn) {
      return false;
    }
    if (!disableCreditCardWhenSufficientCredits) {
      return false;
    }
    if (!balances || !totalCost) {
      return true;
    }

    return isSufficientCredits();
  }, [
    isLoggedIn,
    balances,
    totalCost,
    disableCreditCardWhenSufficientCredits,
    isSufficientCredits
  ]);

  const isCreditCardPaymentDisabled = useMemo(() => {
    if (!ENV_VARIABLES.IS_FIAT_ENABLED) {
      return true;
    }

    // Sub-minimum Stripe totals are floor-charged to $10 + provider fee instead of disabling card.
    return isUSDCPaymentDisabled;
  }, [isUSDCPaymentDisabled]);

  const handlePaymentOptionChange = (option: string) => {
    const key = `${option}-${totalCost}`;

    const isCreditsOrUSDC = option === "credits" || option === "usdc";
    const wasCreditsOrUSDC =
      lastSelectedOptionRef.current === "credits" || lastSelectedOptionRef.current === "usdc";
    if (isCreditsOrUSDC && wasCreditsOrUSDC) {
      if (lastTrackedKeyRef.current !== key) {
        trackEvent(AnalyticsEventType.CLUSTER_PAYMENT_OPTION_SELECTED, {
          payment_type: currency,
          amount: totalCost
        });
        lastTrackedKeyRef.current = key;
      }
    } else {
      pendingOptionRef.current = option;
    }

    lastSelectedOptionRef.current = option;
  };

  useEffect(() => {
    if (!pendingOptionRef.current) return;

    const key = `${pendingOptionRef.current}-${totalCost}`;

    if (lastTrackedKeyRef.current !== key) {
      trackEvent(AnalyticsEventType.CLUSTER_PAYMENT_OPTION_SELECTED, {
        payment_type: currency,
        amount: totalCost
      });
      lastTrackedKeyRef.current = key;
    }

    pendingOptionRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCost]);

  const [currencyField, , currencyHelpers] = useField("currency");
  const currentValue = currencyField.value;

  const handleSelect = (value: string) => {
    currencyHelpers.setValue(value);
    handlePaymentOptionChange(value);
  };

  // Non-credits methods can stay selected after balances update (or auto-default)
  // even when disabled because available credits fully cover compute. Fall back to credits.
  useEffect(() => {
    const isDisabledSelection =
      (currentValue === "stripe" && isCreditCardPaymentDisabled) ||
      (currentValue === "usdc" && isUSDCPaymentDisabled) ||
      (currentValue === "iocoin" && isUSDCPaymentDisabled);
    if (!isDisabledSelection) {
      return;
    }
    if (!getIsCreditsEnabled() || !order.includes("credits")) {
      return;
    }
    currencyHelpers.setValue("credits");
    handlePaymentOptionChange("credits");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue, isCreditCardPaymentDisabled, isUSDCPaymentDisabled, order]);

  const creditCardDisabledTooltip =
    "Credit card payments are disabled because your available credits can cover the full compute cost.";
  const ioCoinDisabledTooltip =
    "IO Coin payments are disabled because your available credits can cover the full compute cost.";

  if (hideCryptoOptions) {
    const options = buildOrderedOptions({
      allowCryptoPayment,
      isUSDCPaymentDisabled,
      isCreditCardPaymentDisabled,
      creditCardDisabledTooltip,
      ioCoinDisabledTooltip,
      order,
      theme,
      showPaymentFees
    });

    return (
      <>
        <RadioCardField
          name="currency"
          className={cn("lg:!grid-cols-1 xl:!grid-cols-2", className)}
          options={options.slice(0, 2)}
          onSelect={handlePaymentOptionChange}
          baseHOCClassName="pb-0"
        />
        <Accordion type="single" collapsible>
          <AccordionItem value="more-payment-options">
            <AccordionTrigger>Pay with Crypto</AccordionTrigger>
            <AccordionContent className="pb-0">
              <RadioCardField
                name="currency"
                className={cn("lg:!grid-cols-1 xl:!grid-cols-2", className)}
                options={options.slice(2)}
                onSelect={handlePaymentOptionChange}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </>
    );
  }

  const horizontalOptions = buildHorizontalOptions({
    allowCryptoPayment,
    isUSDCPaymentDisabled,
    isCreditCardPaymentDisabled,
    creditCardDisabledTooltip,
    ioCoinDisabledTooltip,
    order,
    showPaymentFees
  });

  const isWidePaymentLayout = useMediaQueryMatch({
    customMediaQuery: "(min-width: 1440px)"
  });

  return (
    <div
      className={cn(
        "grid w-full",
        bordered
          ? "divide-x divide-border border border-border"
          : cn(
              "divide-x divide-border",
              isWidePaymentLayout
                ? "divide-y-0"
                : "grid-cols-2 border-t border-border [&>*:nth-child(n+3)]:border-t [&>*:nth-child(n+3)]:border-border"
            ),
        className
      )}
      style={
        bordered || isWidePaymentLayout
          ? { gridTemplateColumns: `repeat(${horizontalOptions.length}, minmax(0, 1fr))` }
          : undefined
      }
    >
      {horizontalOptions.map((option) => {
        const optionButton = (
          <button
            type="button"
            // Use aria-disabled (not disabled) so hover tooltips still work.
            aria-disabled={option.disabled || undefined}
            onClick={() => !option.disabled && handleSelect(option.value)}
            className={cn(
              "flex h-full w-full min-w-0 flex-col overflow-hidden transition-opacity",
              option.disabled && "cursor-not-allowed opacity-50"
            )}
          >
            <div className="flex w-full flex-1 items-start justify-between gap-[8px] px-[16px] py-[16px]">
              <div className="flex min-w-0 flex-1 items-start gap-[8px]">
                <div
                  className={cn(
                    "relative shrink-0",
                    option.iconContainerClassName ?? "size-[20px]"
                  )}
                >
                  {option.icon}
                </div>
                {typeof option.label === "string" ? (
                  <span className="min-w-0 text-[16px] font-medium leading-[24px] text-foreground">
                    {option.label}
                  </span>
                ) : (
                  <div className="min-w-0 flex-1">{option.label}</div>
                )}
              </div>
              <div className="relative ml-[8px] inline-grid shrink-0 place-items-start">
                <div className="col-1 row-1 size-[24px] rounded-[56px] border border-border bg-background" />
                {currentValue === option.value && (
                  <div className="absolute left-[7px] top-[7px] size-[10px] rounded-full bg-[#3179ff]" />
                )}
              </div>
            </div>
          </button>
        );

        if (!option.tooltip) {
          return (
            <div key={option.value} className="flex min-w-0 flex-col">
              {optionButton}
            </div>
          );
        }

        return (
          <SimpleTooltip
            key={option.value}
            id={`payment-option-tooltip-${option.value}`}
            place="top"
            usePortal
            tooltipClassName="!max-w-[300px] whitespace-normal"
            content={<div className="subtext-small text-foreground">{option.tooltip}</div>}
            className="flex h-full min-w-0 flex-col"
          >
            {optionButton}
          </SimpleTooltip>
        );
      })}
    </div>
  );
};

function buildOrderedOptions({
  allowCryptoPayment,
  isUSDCPaymentDisabled,
  isCreditCardPaymentDisabled,
  creditCardDisabledTooltip,
  ioCoinDisabledTooltip,
  order,
  theme,
  showPaymentFees
}: {
  allowCryptoPayment: boolean;
  isUSDCPaymentDisabled: boolean;
  isCreditCardPaymentDisabled: boolean;
  creditCardDisabledTooltip: string;
  ioCoinDisabledTooltip: string;
  order: Array<"credits" | "stripe" | "usdc" | "iocoin">;
  theme?: string;
  showPaymentFees: boolean;
}) {
  const optionMap: Record<string, any | undefined> = {
    credits: getIsCreditsEnabled()
      ? {
          label: (
            <div className="flex items-center gap-2">
              <CreditsIcon className="h-[12px]" inheritColor={theme === "light"} />
              <span className="label-x-small text-foreground">Credits</span>
            </div>
          ),
          value: "credits",
          className: "px-6 py-5 min-h-[96px]"
        }
      : undefined,
    stripe: ENV_VARIABLES.IS_FIAT_ENABLED
      ? {
          label: <CreditCardBrands variant={theme === "light" ? "light" : "dark"} />,
          value: "stripe",
          disabled: isCreditCardPaymentDisabled,
          className: "px-6 py-5 min-h-[96px]",
          description: showPaymentFees ? <PaymentOptionFeeBadge /> : undefined,
          tooltip: isCreditCardPaymentDisabled ? creditCardDisabledTooltip : undefined
        }
      : undefined,
    usdc: allowCryptoPayment
      ? {
          label: <UsdcPaymentLabel />,
          value: "usdc",
          disabled: isUSDCPaymentDisabled,
          className: "px-6 py-5 min-h-[96px] items-center flex",
          description: showPaymentFees ? (
            <PaymentOptionFeeBadge label={ZERO_FEE_LABEL} />
          ) : undefined,
          tooltip: isUSDCPaymentDisabled
            ? "USDC payments are disabled because your available credits can cover the full compute cost."
            : undefined
        }
      : undefined,
    iocoin: allowCryptoPayment
      ? {
          label: <IoCoin />,
          value: "iocoin",
          disabled: isUSDCPaymentDisabled,
          className: "px-6 py-5 min-h-[96px] items-center flex",
          tooltip: isUSDCPaymentDisabled ? ioCoinDisabledTooltip : undefined
        }
      : undefined
  };

  return order.map((key) => optionMap[key]).filter((opt) => Boolean(opt)) as {
    label: React.ReactNode;
    value: string;
    className?: string;
    description?: React.ReactNode;
    disabled?: boolean;
    tooltip?: string;
  }[];
}

function buildHorizontalOptions({
  allowCryptoPayment,
  isUSDCPaymentDisabled,
  isCreditCardPaymentDisabled,
  creditCardDisabledTooltip,
  ioCoinDisabledTooltip,
  order,
  showPaymentFees
}: {
  allowCryptoPayment: boolean;
  isUSDCPaymentDisabled: boolean;
  isCreditCardPaymentDisabled: boolean;
  creditCardDisabledTooltip: string;
  ioCoinDisabledTooltip: string;
  order: Array<"credits" | "stripe" | "usdc" | "iocoin">;
  showPaymentFees: boolean;
}) {
  const optionMap: Record<
    string,
    | {
        icon: React.ReactNode;
        label: React.ReactNode;
        value: string;
        disabled?: boolean;
        tooltip?: string;
      }
    | undefined
  > = {
    credits: getIsCreditsEnabled()
      ? {
          icon: (
            <img
              src="/assets/images/figma/payment-iocoin-icon.svg"
              alt="Credits"
              className="size-full object-contain"
            />
          ),
          label: "Credits",
          value: "credits"
        }
      : undefined,
    stripe: ENV_VARIABLES.IS_FIAT_ENABLED
      ? {
          icon: <CreditCardIcon className="size-full text-foreground" />,
          label: (
            <div className="flex min-w-0 flex-1 flex-col items-center gap-2">
              <span className="text-[16px] font-medium leading-[24px] text-foreground">
                Credit Card
              </span>
              <CreditCardBrands />
              {showPaymentFees ? <PaymentOptionFeeBadge /> : null}
            </div>
          ),
          value: "stripe",
          disabled: isCreditCardPaymentDisabled,
          tooltip: isCreditCardPaymentDisabled ? creditCardDisabledTooltip : undefined
        }
      : undefined,
    usdc: allowCryptoPayment
      ? {
          icon: (
            <img
              src="/assets/images/figma/payment-usdc-logo.svg"
              alt="USD backed stablecoin"
              className="size-full object-contain"
            />
          ),
          label: (
            <div className="flex min-w-0 flex-1 flex-col items-center gap-2">
              <UsdcPaymentLabel />
              {showPaymentFees ? <PaymentOptionFeeBadge label={ZERO_FEE_LABEL} /> : null}
            </div>
          ),
          value: "usdc",
          disabled: isUSDCPaymentDisabled,
          tooltip: isUSDCPaymentDisabled
            ? "USDC payments are disabled because your available credits can cover the full compute cost."
            : undefined
        }
      : undefined,
    iocoin: allowCryptoPayment
      ? {
          icon: (
            <img
              src="/assets/images/figma/payment-iocoin-icon.svg"
              alt="IO Coin"
              className="size-full object-contain"
            />
          ),
          label: "Coin",
          value: "iocoin",
          disabled: isUSDCPaymentDisabled,
          tooltip: isUSDCPaymentDisabled ? ioCoinDisabledTooltip : undefined
        }
      : undefined
  };

  return order.map((key) => optionMap[key]).filter(Boolean) as {
    icon: React.ReactNode;
    iconContainerClassName?: string;
    label: React.ReactNode;
    value: string;
    disabled?: boolean;
    tooltip?: string;
  }[];
}

export { CreditCardBrands };
